import { EXTRAS_TYPE } from '@ahmdigital/constants';
import { includes, isEmpty, map } from 'lodash/fp';

import { Customer } from '../selectors/select-customer/types';
import { EVENTS, VALID_EVENTS_ECOMMERCE_GA4 } from './google/constants';
import { ProductType } from '../types/product';
import { RootState } from '../reducers';
import constants from '../ahm-constants';
import GA4Analytics from './ga4-analytics';
import getPartnerAuthText from '../pages/buy/components/buy-form/components/steps/about/utils/get-partner-auth-text';
import PriceService, { Price, Prices } from '../models/price';
import ProductService from '../models/product';
import selectCalculatedYouthDiscountPercentage from '../selectors/select-calculated-youth-discount-percentage';
import selectSwitchingMessageBundle from '../pages/buy/components/buy-form/components/steps/success/components/switching-message/selectors/select-switching-message-bundle';
import utils, { encryptUserData } from './utils';

/* eslint-disable camelcase */
// snake_case keys are used here as per GA spec
const itemMapper = (product: ProductType, price: number | undefined) => ({
  item_brand: 'PHI',
  item_category: product?.type,
  item_category2: product?.name,
  item_category3: product?.extrasType === EXTRAS_TYPE.CHOOSABLE ? product?.shortName : '',
  item_id: product?.id,
  item_name: product?.name,
  item_variant: product?.charges?.excess?.amount ? `${product?.charges?.excess?.amount} excess` : 'no excess',
  price,
  quantity: 1,
});
/* eslint-enable camelcase */

const getPriceFromPriceObject = (priceObject: Price | undefined, frequency: Customer['frequency']) =>
  priceObject ? PriceService.getAmountAsFloat(priceObject, frequency) : undefined;

export type EcommerceGA4Data = {
  email?: string;
  coupon?: string;
  customer?: Customer;
  marketable?: 'yes' | 'no';
  phoneNumber?: string;
  price?: Price;
  prices?: Prices;
  product?: ProductType;
  products?: ProductType[];
  type: string;
};

const ecommerceHandlerGA4 = (data: EcommerceGA4Data, state: RootState) => {
  const type = data?.type;

  if (!includes(type, VALID_EVENTS_ECOMMERCE_GA4)) {
    throw new Error(`Unsupported GA4 ecommerce event type '${type}'`);
  }

  const scale = state?.customer?.scale;
  const frequency = state?.customer?.frequency;
  const coupon = isEmpty(data?.coupon) ? 'no' : data?.coupon;
  const lhc = state?.customer?.lhc;
  const hasLhc = lhc > 0 ? 'yes' : 'no';
  const prices = state?.price?.data;
  const marketable = data?.marketable;

  const calculatedYouthDiscountPercentage = selectCalculatedYouthDiscountPercentage(state);

  const hasYouthDiscount = calculatedYouthDiscountPercentage > 0 ? 'yes' : 'no';

  /* eslint-disable camelcase */
  const userProperties = {
    household_income_type: state?.customer?.incomeType ?? 'unknown',
    income_tier: state?.customer?.incomeTier,
    partner_authorisation: getPartnerAuthText(state?.checkout?.buy?.partnerAuthorisation as string),
    previous_health_fund: state?.checkout?.buy?.existingHealthFund ?? 'unknown',
    state: state?.customer?.state ?? '',
  };

  const basePayload = {
    ...userProperties,
    coupon,
    currency: 'AUD',
    lifetime_health_cover_loading: hasLhc,
    marketable,
    payment_frequency: frequency,
    scale,
    youth_discount: hasYouthDiscount,
  };
  /* eslint-enable camelcase */

  // view_item_list is handled separately in this if statement,
  // because the items array includes many products rather than one.
  if (type === EVENTS.ECOMMERCE_GA4.VIEW_ITEM_LIST) {
    const products = data?.products;
    const customer = data?.customer;
    const productType = data?.products?.[0]?.type;

    const items = map((product) => {
      const priceObject = PriceService.getPrice(prices, product?.id, customer ?? {});
      const price = getPriceFromPriceObject(priceObject, 'yearly');
      return itemMapper(product, price);
    }, products);

    const viewItemListPayload = {
      ...basePayload,
      items,
      pseu: productType && scale ? utils.getPseu(productType, scale) : undefined,
    };

    GA4Analytics.submit({ data: viewItemListPayload, name: EVENTS.ECOMMERCE_GA4.VIEW_ITEM_LIST });
    return;
  }

  const product = data?.product || ProductService.getProduct(state?.product?.data, state?.cart?.product?.id);
  const productId = product?.id;
  const priceObject = data?.price ?? PriceService.getPrice(prices, productId, data.customer ?? {});
  const transactionId = `${productId}:${Date.now()}`;
  const yearlyPrice = getPriceFromPriceObject(priceObject, 'yearly');
  const recurringPrice = getPriceFromPriceObject(priceObject, frequency);
  const productType = product?.type;
  const item = itemMapper(product, yearlyPrice);

  /* eslint-disable camelcase */
  const payload = {
    ...basePayload,
    items: [item],
    pseu: productType && scale ? utils.getPseu(productType, scale) : undefined,
    recurring_price: recurringPrice,
    value: yearlyPrice,
  };

  if (type === EVENTS.ECOMMERCE_GA4.PURCHASE) {
    // Existing health fund is cleared from checkout object in redux so we can grab it from the membership object instead
    const previousHealthFund =
      selectSwitchingMessageBundle(state)?.customers?.[constants.RELATIONSHIP.CLIENT]?.existingHealthFund ?? 'unknown';

    encryptUserData(data.email, data.phoneNumber).then((userData) => {
      GA4Analytics.submit({
        data: {
          ...payload,
          membership_id: state?.membership?.membershipId,
          previous_health_fund: previousHealthFund,
          transaction_id: transactionId,
          user_data: userData,
          user_properties: {
            ...userProperties,
            membership_id: state?.membership?.membershipId,
            previous_health_fund: previousHealthFund,
          },
        },
        name: type,
      });
    });

    return;
  }
  /* eslint-enable camelcase */

  GA4Analytics.submit({ data: payload, name: type });
};

export default ecommerceHandlerGA4;
